import { tt } from './i18n'

/**
 * Blocked e-mails, phone numbers and IP addresses - `FRONTEND.md` §9.787, on BACKEND.md §10A.439, owner O140: *"Možnosť
 * zablokovať email/telefonné číslo - či IP adresu, odkiaľ prišla objednávka - proti zlým objednávkam"*.
 *
 * **One reader for three screens**: *Zablokované* in Marketing, the customer drawer in *Zákazníci*, and the order
 * archive and the till in the orders module - which may not import each other (§5), so the shape lives here.
 */

export type BlockKind = 'email' | 'phone' | 'ip'

export const BLOCK_KINDS: readonly BlockKind[] = ['phone', 'email', 'ip']

export const BLOCKED_CONTACTS_PATH = '/api/v1/blocked-contacts'
export const BLOCKED_CONTACTS_KEY = ['blocked-contacts'] as const

export function blockedContactPath(id: number): string {
  return `${BLOCKED_CONTACTS_PATH}/${String(id)}`
}

export function orderBlockPath(orderId: number): string {
  return `/api/v1/orders/${String(orderId)}/block`
}

export function customerBlockPath(customerId: number): string {
  return `/api/v1/customers/${String(customerId)}/block`
}

export function blockMatchPath(contact: { phone: string; email: string }): string {
  const query = new URLSearchParams()
  if (contact.phone.trim() !== '') query.set('phone', contact.phone.trim())
  if (contact.email.trim() !== '') query.set('email', contact.email.trim())
  return `${BLOCKED_CONTACTS_PATH}/match?${query.toString()}`
}

export interface BlockedContact {
  id: number
  kind: BlockKind
  shown: string
  reason: string | null
  customerId: number | null
  customerName: string | null
  orderId: number | null
  orderNumber: string | null
  createdBy: string | null
  attempts: number
  lastAttemptAt: string | null
  createdAt: string | null
}

export interface BlockMatch {
  kind: BlockKind
  shown: string
  reason: string | null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

function kindOf(value: unknown): BlockKind | null {
  return value === 'email' || value === 'phone' || value === 'ip' ? value : null
}

function rowsOf(payload: unknown): Record<string, unknown>[] {
  return Array.isArray(payload)
    ? payload.filter((one): one is Record<string, unknown> => typeof one === 'object' && one !== null)
    : []
}

/** The list, or null when the answer is not one - which a screen reports rather than draws as *nothing blocked*. */
export function blockedContactsOf(payload: unknown): BlockedContact[] | null {
  if (!Array.isArray(payload)) return null
  return rowsOf(payload).flatMap((row) => {
    const kind = kindOf(row.kind)
    if (typeof row.id !== 'number' || kind === null) return []
    return [
      {
        id: row.id,
        kind,
        shown: text(row.shown) ?? '',
        reason: text(row.reason),
        customerId: typeof row.customerId === 'number' ? row.customerId : null,
        customerName: text(row.customerName),
        orderId: typeof row.orderId === 'number' ? row.orderId : null,
        orderNumber: text(row.orderNumber),
        createdBy: text(row.createdBy),
        attempts: typeof row.attempts === 'number' ? row.attempts : 0,
        lastAttemptAt: text(row.lastAttemptAt),
        createdAt: text(row.createdAt),
      },
    ]
  })
}

/** Whether a customer is blocked, and by which entries - null when the answer carries no verdict. */
export function customerBlockOf(payload: unknown): { blocked: boolean; entries: BlockedContact[] } | null {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null
  const row = payload as Record<string, unknown>
  if (typeof row.blocked !== 'boolean') return null
  return { blocked: row.blocked, entries: blockedContactsOf(row.entries) ?? [] }
}

/** What the till's typed phone or e-mail matches. */
export function blockMatchesOf(payload: unknown): BlockMatch[] {
  return rowsOf(payload).flatMap((row) => {
    const kind = kindOf(row.kind)
    return kind === null ? [] : [{ kind, shown: text(row.shown) ?? '', reason: text(row.reason) }]
  })
}

export function blockKindLabel(kind: BlockKind): string {
  if (kind === 'email') return tt('E-mail')
  if (kind === 'phone') return tt('Telefón')
  return tt('IP adresa')
}

/** Where a block came from: the order, the customer, or by hand. */
export function blockSource(block: Pick<BlockedContact, 'orderNumber' | 'customerName'>): string {
  if (block.orderNumber !== null) return `${tt('z objednávky')} #${block.orderNumber}`
  if (block.customerName !== null) return `${tt('zákazník')} ${block.customerName}`
  return tt('ručne')
}

/**
 * Why an IP block is the weakest of the three - said wherever one can be chosen. Mobile networks put many customers
 * behind one address, and until owner question #75 is answered the address a request names can be forged.
 */
export function ipBlockCaveat(): string {
  return tt(
    'IP adresa je najslabšie blokovanie: mobilné siete zdieľajú jednu adresu medzi mnohými zákazníkmi a adresa sa dá zmeniť. Blokujte radšej telefón alebo e-mail.',
  )
}

/** The till's warning for one match. */
export function blockMatchNote(match: BlockMatch): string {
  const what = `${blockKindLabel(match.kind)} ${match.shown}`
  // *Na zozname zablokovaných* rather than *zablokovaný*: an IP address is feminine, and one sentence serves all three.
  const base = `${what} ${tt('je na zozname zablokovaných - objednávku z webu by sme odmietli.')}`
  return match.reason === null ? base : `${base} ${tt('Dôvod')}: ${match.reason}`
}
